import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import AuthCallback from './auth/AuthCallback';
import { PS } from './theme';
import { Wordmark } from './lib/ui';
import GuestGate from './components/GuestGate';
import OfflineBar from './components/OfflineBar';
import { Toaster } from 'sonner';

import Landing from './pages/Landing';
import HostLanding from './pages/HostLanding';
import About from './pages/About';
import Home from './pages/Home';
import Discover from './pages/Discover';
import CircleDetail from './pages/CircleDetail';
import RecordBid from './pages/RecordBid';
import Chat from './pages/Chat';
import Wallet from './pages/Wallet';
import Profile from './pages/Profile';
import EvidenceVault from './pages/EvidenceVault';
import Dispute from './pages/Dispute';
import CreateCircle from './pages/CreateCircle';
import HostConsole from './pages/HostConsole';
import Learn from './pages/Learn';
import Article from './pages/Article';
import Debug from './pages/Debug';
import DevLogin from './pages/DevLogin';
import VerifyPackage from './pages/VerifyPackage';
import GuideIndex from './pages/GuideIndex';
import GuideArticle from './pages/GuideArticle';
import Admin from './pages/Admin';

function FullLoader() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: PS.page }}>
      <Wordmark size={28} />
      <div className="ps-live-dot" style={{ fontSize: 13, color: PS.textMuted }}>กำลังโหลด…</div>
    </div>
  );
}

/** Gated route: members get the page; guests get a friendly login gate (no redirect). */
function Gated({ children, gate }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return gate;
  return children;
}

function PublicHome() {
  const { loading } = useAuth();
  if (loading) return <FullLoader />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/login" element={<PublicHome />} />
      <Route path="/about" element={<About />} />
      <Route path="/host" element={<HostLanding />} />
      <Route path="/for-hosts" element={<HostLanding />} />
      <Route path="/verify" element={<VerifyPackage />} />
      <Route path="/guide" element={<GuideIndex />} />
      <Route path="/guide/:slug" element={<GuideArticle />} />

      {/* Public / guest-accessible (explore & study before joining) */}
      <Route path="/app" element={<Home />} />
      <Route path="/app/discover" element={<Discover />} />
      <Route path="/app/learn" element={<Learn />} />
      <Route path="/app/learn/:id" element={<Article />} />
      <Route path="/app/circle/:id" element={<CircleDetail />} />
      <Route path="/app/debug" element={<Debug />} />
      <Route path="/app/dev-login" element={<DevLogin />} />

      {/* Gated actions — guests see a contextual login gate */}
      <Route path="/app/create" element={<Gated gate={<GuestGate icon="plus" testid="gate-create" title="ตั้งวงแชร์ของคุณ" message="เข้าสู่ระบบเพื่อเป็นท้าวแชร์ — ระบบจะช่วยตรวจกฎหมายให้อัตโนมัติและเก็บหลักฐานทุกอย่างให้" />}><CreateCircle /></Gated>} />
      <Route path="/app/chat" element={<Gated gate={<GuestGate icon="chat" testid="gate-chat" title="แชทกลุ่มวงแชร์" message="เข้าสู่ระบบเพื่อคุยกับเพื่อนร่วมวง — ทุกข้อความถูกบันทึกเป็นหลักฐาน" />}><Chat /></Gated>} />
      <Route path="/app/me" element={<Gated gate={<GuestGate icon="user" testid="gate-me" title="โปรไฟล์ & เครดิตของคุณ" message="เข้าสู่ระบบเพื่อดูคะแนนเครดิต ประวัติการเล่น และวงที่คุณเข้าร่วม" />}><Profile /></Gated>} />
      <Route path="/app/wallet" element={<Gated gate={<GuestGate icon="wallet" testid="gate-wallet" title="การจ่ายเงิน & สลิป" message="เข้าสู่ระบบเพื่อดูยอดที่ต้องจ่ายและแนบสลิปเป็นหลักฐาน" />}><Wallet /></Gated>} />
      <Route path="/app/circle/:id/bid" element={<Gated gate={<GuestGate layout="screen" icon="arrowUp" testid="gate-bid" title="บันทึกผลการเปีย" message="เข้าสู่ระบบเพื่อบันทึกผลการประมูลเป็นหลักฐาน" />}><RecordBid /></Gated>} />
      <Route path="/app/circle/:id/chat" element={<Gated gate={<GuestGate layout="screen" icon="chat" testid="gate-cchat" title="แชทกลุ่ม" message="เข้าสู่ระบบเพื่อเข้าร่วมแชทของวงนี้" />}><Chat /></Gated>} />
      <Route path="/app/circle/:id/vault" element={<Gated gate={<GuestGate layout="screen" icon="shield" testid="gate-vault" title="ห้องนิรภัยหลักฐาน" message="หลักฐานของวงเปิดให้เฉพาะสมาชิก เข้าสู่ระบบเพื่อดูไทม์ไลน์ที่ลงลายเซ็นแล้ว" />}><EvidenceVault /></Gated>} />
      <Route path="/app/circle/:id/dispute" element={<Gated gate={<GuestGate layout="screen" icon="lock" testid="gate-dispute" title="ขอเอกสารดำเนินคดี" message="เข้าสู่ระบบเพื่อขอซองหลักฐานปิดผนึก" />}><Dispute /></Gated>} />
      <Route path="/app/host/:id" element={<Gated gate={<GuestGate layout="screen" icon="coin" testid="gate-host" title="คอนโซลท้าวแชร์" message="เข้าสู่ระบบเพื่อจัดการวงแชร์ของคุณ" />}><HostConsole /></Gated>} />
      <Route path="/app/admin" element={<Gated gate={<GuestGate icon="shield" testid="gate-admin" title="ผู้ดูแลระบบ" message="เข้าสู่ระบบด้วยบัญชีผู้ดูแลเพื่อจัดการการตั้งค่าระบบ" />}><Admin /></Gated>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <OfflineBar />
        <Toaster position="top-center" richColors closeButton offset="64px" toastOptions={{ style: { fontFamily: PS.fontUI, borderRadius: '14px' } }} />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
